import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import type { DailyGoal } from '../lib/database.types';

export function useGoals() {
  const { user } = useAuth();
  const [goals, setGoals] = useState<DailyGoal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchGoals();
    }
  }, [user]);

  const fetchGoals = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('daily_goals')
        .select('*')
        .eq('user_id', user.id)
        .order('goal_date', { ascending: false })
        .limit(30);

      if (error) throw error;
      setGoals(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const createGoal = async (goalText: string, goalDate?: string) => {
    if (!user) return { error: new Error('No user logged in') };

    const date = goalDate || new Date().toISOString().split('T')[0];

    try {
      const { data, error } = await supabase
        .from('daily_goals')
        .insert({
          user_id: user.id,
          goal_date: date,
          goal_text: goalText,
        })
        .select()
        .single();

      if (error) throw error;

      setGoals(prev => [data, ...prev.filter(g => g.goal_date !== date)]);
      return { data, error: null };
    } catch (err) {
      return { error: err };
    }
  };

  const updateGoal = async (goalId: string, updates: Partial<DailyGoal>) => {
    if (!user) return { error: new Error('No user logged in') };

    try {
      const { data, error } = await supabase
        .from('daily_goals')
        .update(updates)
        .eq('id', goalId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      setGoals(prev => prev.map(g => g.id === goalId ? data : g));
      return { data, error: null };
    } catch (err) {
      return { error: err };
    }
  };

  const completeGoal = async (goalId: string, responseMethod: 'text' | 'voice' = 'text') => {
    return updateGoal(goalId, {
      completed: true,
      completion_time: new Date().toISOString(),
      response_method: responseMethod,
    });
  };

  const getTodaysGoal = () => {
    const today = new Date().toISOString().split('T')[0];
    return goals.find(goal => goal.goal_date === today);
  };

  const getWeeklyStats = () => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    const weeklyGoals = goals.filter(goal => 
      new Date(goal.goal_date) >= oneWeekAgo
    );
    
    const completed = weeklyGoals.filter(goal => goal.completed).length;
    return { completed, total: weeklyGoals.length };
  };

  const getMonthlyCompletionRate = () => {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    
    const monthlyGoals = goals.filter(goal => 
      new Date(goal.goal_date) >= oneMonthAgo
    );
    
    if (monthlyGoals.length === 0) return 0;
    
    const completed = monthlyGoals.filter(goal => goal.completed).length;
    return Math.round((completed / monthlyGoals.length) * 100);
  };

  const getCurrentStreak = () => {
    const sortedGoals = [...goals].sort((a, b) => 
      new Date(b.goal_date).getTime() - new Date(a.goal_date).getTime()
    );
    
    let streak = 0;
    for (const goal of sortedGoals) {
      if (goal.completed) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  };

  return {
    goals,
    loading,
    error,
    createGoal,
    updateGoal,
    completeGoal,
    getTodaysGoal,
    getWeeklyStats,
    getMonthlyCompletionRate,
    getCurrentStreak,
    refetch: fetchGoals,
  };
}